// src/components/CallLog/LogCallButton.jsx  ← UPDATED (Step 8)
// ─────────────────────────────────────────────────────────────────────────────
// Smart button: opens Day10CallLog for day10, DayNCallLog for day30–day180.
//
// Props:
//   employee  — full employee object
//   callKey   — "day10" | "day30" | "day60" | "day90" | "day120" | "day150" | "day180"
//   onSaved() — optional callback after save
//   size      — Ant Button size (default "small")
//   style     — optional extra inline styles
//   label     — optional override for button text
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState } from "react";
import { Button, Tooltip } from "antd";
import { PhoneOutlined, EditOutlined } from "@ant-design/icons";
import Day10CallLog from "./Day10CallLog";
import DayNCallLog  from "./DayNCallLog";
import { useScheduleForCohort } from "../../context/MasterDataContext";

export default function LogCallButton({
  employee,
  callKey,
  onSaved,
  size  = "small",
  style = {},
  label,
}) {
  const [open, setOpen] = useState(false);
  // Resolve the employee's own schedule so we know if this stage has Q&A
  const callSchedule    = useScheduleForCohort(employee?.cohortName);

  if (!employee || !callKey) return null;

  const existing     = employee.calls?.[callKey];
  const isCompleted  = existing?.status === "Completed";
  const schedEntry   = callSchedule.find((s) => s.key === callKey);
  const callLabel    = schedEntry?.label || callKey.replace("day", "Day-");
  // hasQuestions: true means this stage uses the structured Q&A form (like Day10)
  const hasQuestions = schedEntry?.hasQuestions ?? (callKey === "day10");

  const btnLabel = label || (isCompleted ? `Edit ${callLabel}` : `Log ${callLabel}`);
  const btnIcon  = isCompleted ? <EditOutlined /> : <PhoneOutlined />;
  const btnColor = isCompleted ? "#7a96b0" : "#1E6FBF";
  const btnBg    = isCompleted ? "#f5f8fc" : "#eaf1f8";
  const btnBorder= isCompleted ? "#dce6f0" : "#c5d6e8";

  function handleSaved() {
    setOpen(false);
    onSaved?.();
  }

  const sharedBtnStyle = {
    borderRadius: 8,
    borderColor:  btnBorder,
    color:        btnColor,
    background:   btnBg,
    fontWeight:   600,
    fontSize:     11,
    ...style,
  };

  return (
    <>
      <Tooltip title={btnLabel}>
        <Button size={size} icon={btnIcon} onClick={() => setOpen(true)} style={sharedBtnStyle}>
          {btnLabel}
        </Button>
      </Tooltip>

      {/* Open Day10CallLog (structured Q&A) for any stage with hasQuestions=true */}
      {hasQuestions && (
        <Day10CallLog
          open={open}
          employee={employee}
          callKey={callKey}
          onClose={() => setOpen(false)}
          onSaved={handleSaved}
        />
      )}

      {/* Open standard DayNCallLog for stages without Q&A */}
      {!hasQuestions && (
        <DayNCallLog
          open={open}
          employee={employee}
          callKey={callKey}
          onClose={() => setOpen(false)}
          onSaved={handleSaved}
        />
      )}
    </>
  );
}